import React from "react"
import toast from "react-hot-toast"
import { Html5Qrcode } from "html5-qrcode"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, TableWrap, Btn, Badge } from "../shared/components"
import {
  listSessions,
  startSessionQR,
  endSession,
  getCurrentSessionQR,
  getSession,
  listAttendees,
  scanAttendance,
  checkInWithCode,
} from "../api/sessions.api"

const QR_POLL_MS = 5000          // refresh QR png every 5s (token TTL 30s)
const ATTENDEES_POLL_MS = 5000   // refresh attendees every 5s

export default function QRAttendanceScreen() {
  const { role } = useApp()
  if (role === "participant") return <ParticipantScannerView />
  return <TrainerGeneratorView />
}

// ─────────────────────────────────────────────────────────────────────────────
// TRAINER / ADMIN — generate rotating QR + live attendees
// ─────────────────────────────────────────────────────────────────────────────
function TrainerGeneratorView() {
  const { lang, currentProgramId, currentProgram, currentClassId, pageState } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const [sessions, setSessions] = React.useState([])
  const [selectedId, setSelectedId] = React.useState(null)
  const [qrData, setQrData] = React.useState(null)   // { token, shortCode, qrPngDataUrl, expiresAt }
  const [attendees, setAttendees] = React.useState([])
  const [starting, setStarting] = React.useState(false)
  const [now, setNow] = React.useState(Date.now())
  // Sesi yang dibuka langsung lewat tombol QR. Disimpan terpisah karena daftar
  // `sessions` menyaring sesi yang sudah lewat lebih dari 24 jam.
  const [sesiTerpilih, setSesiTerpilih] = React.useState(null)
  const sudahDibuka = React.useRef(false)

  // Tick clock for countdown
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500)
    return () => clearInterval(id)
  }, [])

  // Load sessions
  React.useEffect(() => {
    if (!currentProgramId) return
    listSessions(currentProgramId, currentClassId)
      .then((all) => {
        const upcoming = all.filter((s) => {
          const diff = new Date(s.scheduled_at) - new Date()
          return diff > -24 * 60 * 60 * 1000 // up to 24h past
        })
        setSessions(upcoming)
      })
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
  }, [currentProgramId, currentClassId])

  const startSession = async (s) => {
    setStarting(true)
    try {
      const res = await startSessionQR(s.id)
      setSelectedId(s.id)
      setQrData(res)
      toast.success(lang === "id" ? "Sesi dimulai" : "Session started")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setStarting(false)
    }
  }

  // Sesi yang sudah live dilanjutkan, bukan dimulai ulang: startSessionQR selalu
  // memutar token baru, jadi peserta yang sedang mengetik kode yang tampil
  // mendadak ditolak.
  const resumeSession = async (s) => {
    setStarting(true)
    try {
      const cur = await getCurrentSessionQR(s.id)
      setSelectedId(s.id)
      setQrData(cur)
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setStarting(false)
    }
  }

  // Dibuka dari tombol QR / Attendance dengan sessionId: langsung ke QR sesi itu,
  // tanpa lewat daftar "Pilih Sesi". Ref menjaga StrictMode (efek dijalankan dua
  // kali di dev) supaya sesi tidak dimulai dua kali.
  React.useEffect(() => {
    const id = pageState?.sessionId
    if (!id || sudahDibuka.current) return
    sudahDibuka.current = true
    getSession(id)
      .then((s) => {
        setSesiTerpilih(s)
        return s.is_active ? resumeSession(s) : startSession(s)
      })
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState?.sessionId])

  // End the session on the backend (is_active → false), then clear local state.
  const stopSession = async () => {
    const id = selectedId
    setSelectedId(null)
    setQrData(null)
    setAttendees([])
    if (!id) return
    try {
      await endSession(id)
      toast.success(lang === "id" ? "Sesi diakhiri" : "Session ended")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }

  // Poll current QR while session active
  React.useEffect(() => {
    if (!selectedId) return
    const tick = async () => {
      try {
        const cur = await getCurrentSessionQR(selectedId)
        setQrData(cur)
      } catch (e) {
        console.warn("[qr poll]", e.message)
      }
    }
    const id = setInterval(tick, QR_POLL_MS)
    return () => clearInterval(id)
  }, [selectedId])

  // Poll attendees
  React.useEffect(() => {
    if (!selectedId) return
    const tick = async () => {
      try {
        const list = await listAttendees(selectedId)
        setAttendees(list)
      } catch (e) {
        console.warn("[attendees poll]", e.message)
      }
    }
    tick()
    const id = setInterval(tick, ATTENDEES_POLL_MS)
    return () => clearInterval(id)
  }, [selectedId])

  const expiresInSec = qrData?.expiresAt
    ? Math.max(0, Math.floor((new Date(qrData.expiresAt) - now) / 1000))
    : 0

  const activeSession =
    sessions.find((s) => s.id === selectedId) ||
    (sesiTerpilih?.id === selectedId ? sesiTerpilih : null)

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.code?.toUpperCase() || orgName} · {lang === "id" ? "Sesi" : "Sessions"}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {lang === "id" ? "Absensi" : "Attendance"}
          </h1>
          <div className="muted mt-1">
            {lang === "id"
              ? "Mulai sesi dan tampilkan QR yang berputar tiap 30 detik. Peserta scan dengan menu Absensi."
              : "Start a session and display a QR that rotates every 30 seconds. Participants scan via the Attendance menu."}
          </div>
        </div>
      </div>

      {!selectedId ? (
        <div className="card mt-4">
          <div className="card-header">
            <div className="h3">{lang === "id" ? "Pilih Sesi" : "Pick a Session"}</div>
            <span className="card-header-sub">{currentProgram?.name}</span>
          </div>
          {sessions.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center" }} className="muted small">
              {lang === "id"
                ? "Belum ada sesi terjadwal. Buat dulu di menu Sesi."
                : "No upcoming sessions. Create one in the Sesi menu first."}
            </div>
          ) : (
            <TableWrap>
            <table className="tbl">
              <thead><tr>
                <th>{lang === "id" ? "Judul" : "Title"}</th>
                <th>{lang === "id" ? "Jadwal" : "Schedule"}</th>
                <th>{lang === "id" ? "Lokasi" : "Location"}</th>
                <th></th>
              </tr></thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{s.title}</div>
                      {s.is_active && <Badge tone="success" dot style={{ marginTop: 2 }}>Live</Badge>}
                    </td>
                    <td className="mono small">
                      {new Date(s.scheduled_at).toLocaleString(lang === "id" ? "id-ID" : "en-US", {
                        weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="small">{s.location || "—"}</td>
                    <td>
                      <Btn variant="primary" size="sm" icon={<Icon name="qrCode" size={12}/>}
                        disabled={starting} onClick={() => (s.is_active ? resumeSession(s) : startSession(s))}>
                        {starting
                          ? "..."
                          : s.is_active
                            ? (lang === "id" ? "Lanjutkan" : "Resume")
                            : (lang === "id" ? "Mulai Sesi" : "Start Session")}
                      </Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </TableWrap>
          )}
        </div>
      ) : (
        <div className="grid mt-4" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 380px", gap: 16 }}>
          {/* QR display */}
          <div className="card">
            <div className="card-header">
              <div className="h3">{activeSession?.title}</div>
              <Btn variant="ghost" size="sm" icon={<Icon name="close" size={12}/>} onClick={stopSession}>
                {lang === "id" ? "Akhiri Sesi" : "End Session"}
              </Btn>
            </div>
            <div className="col" style={{ alignItems: "center", padding: 32, gap: 20 }}>
              {qrData?.qrPngDataUrl ? (
                <div style={{
                  padding: 16,
                  background: "white",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 12,
                  boxShadow: "var(--shadow-md)",
                }}>
                  <img
                    src={qrData.qrPngDataUrl}
                    alt="Session QR"
                    style={{ width: "min(320px, 70vw)", aspectRatio: "1 / 1", display: "block" }}
                  />
                </div>
              ) : (
                <div style={{
                  width: "min(320px, 70vw)", aspectRatio: "1 / 1",
                  background: "var(--surface-2)", border: "1px solid var(--border-subtle)",
                  borderRadius: 12, display: "grid", placeItems: "center",
                  color: "var(--text-tertiary)", fontSize: 13,
                }}>
                  {lang === "id" ? "Memuat QR..." : "Loading QR..."}
                </div>
              )}
              {qrData?.shortCode && (
                <div className="col" style={{ alignItems: "center", gap: 2 }}>
                  <span className="tiny muted">{lang === "id" ? "atau ketik kode" : "or enter code"}</span>
                  <span className="mono tnum" style={{ fontSize: 40, fontWeight: 600, letterSpacing: "0.12em" }}>
                    {`${qrData.shortCode.slice(0, 3)} ${qrData.shortCode.slice(3)}`}
                  </span>
                </div>
              )}
              <div className="row gap-3" style={{ alignItems: "center" }}>
                <Badge tone="success" dot>Live</Badge>
                <div className="mono tnum small">
                  {lang === "id" ? "Ganti dalam" : "Rotates in"}: <strong>{expiresInSec}s</strong>
                </div>
              </div>
              <div className="tiny muted" style={{ textAlign: "center", maxWidth: 320 }}>
                {lang === "id"
                  ? "Tampilkan layar ini ke peserta. QR dan kode diganti otomatis tiap ~30 detik untuk mencegah penyalahgunaan."
                  : "Show this screen to participants. The QR and code auto-rotate every ~30s to prevent abuse."}
              </div>
            </div>
          </div>

          {/* Attendees */}
          <div className="card">
            <div className="card-header">
              <div className="h3">{lang === "id" ? "Kehadiran Live" : "Live Attendance"}</div>
              <span className="card-header-sub">{attendees.length}</span>
            </div>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border-subtle)" }}>
              <Stat label={lang === "id" ? "Hadir" : "Scanned"} value={String(attendees.length)} />
            </div>
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              {attendees.length === 0 ? (
                <div className="muted small" style={{ padding: 24, textAlign: "center" }}>
                  {lang === "id" ? "Belum ada yang scan." : "No scans yet."}
                </div>
              ) : attendees.map((a) => (
                <div key={a.id} className="row gap-3" style={{
                  padding: "10px 16px",
                  borderTop: "1px solid var(--border-subtle)",
                  alignItems: "center",
                }}>
                  <div style={{
                    width: 8, height: 8, background: "var(--green-50)",
                    borderRadius: "50%", flexShrink: 0,
                  }}/>
                  <div className="col" style={{ flex: 1, minWidth: 0 }}>
                    <span className="small text-truncate" style={{ fontWeight: 500 }}>
                      {a.user?.full_name || "—"}
                    </span>
                    <span className="tiny muted text-truncate">{a.user?.email}</span>
                  </div>
                  <span className="tiny mono muted">
                    {new Date(a.scanned_at).toLocaleTimeString(lang === "id" ? "id-ID" : "en-US", {
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PARTICIPANT — camera scanner
// ─────────────────────────────────────────────────────────────────────────────
// Koordinat untuk geo-fence, atau {} kalau izin lokasi ditolak/lambat. Dipakai
// jalur QR maupun jalur kode — tanpa ini absen lewat kode lolos dari geo-fence.
function ambilKoordinat() {
  if (!navigator.geolocation) return Promise.resolve({})
  return new Promise((resolve) =>
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({}),
      { timeout: 3000 }
    )
  )
}

function ParticipantScannerView() {
  const { lang } = useApp()
  const [scanning, setScanning] = React.useState(false)
  const [lastResult, setLastResult] = React.useState(null)
  const [cameras, setCameras] = React.useState([])
  const [selectedCamId, setSelectedCamId] = React.useState(null)
  const [errorMsg, setErrorMsg] = React.useState(null)
  const [kode, setKode] = React.useState("")
  const [mengirimKode, setMengirimKode] = React.useState(false)
  const html5QrRef = React.useRef(null)
  const lastTokenRef = React.useRef(null)

  const handleScanned = React.useCallback(async (decodedText) => {
    let token = decodedText
    try {
      const parsed = JSON.parse(decodedText)
      if (parsed?.t) token = parsed.t
    } catch { /* QR was raw token, use as-is */ }

    if (lastTokenRef.current === token) return
    lastTokenRef.current = token

    try {
      const res = await scanAttendance(token, await ambilKoordinat())
      setLastResult({ ok: true, title: res.sessionTitle })
      toast.success(lang === "id" ? `Hadir dicatat: ${res.sessionTitle}` : `Checked in: ${res.sessionTitle}`)
    } catch (e) {
      const msg = e?.response?.data?.error || e.message
      setLastResult({ ok: false, error: msg })
      toast.error(msg)
      setTimeout(() => { lastTokenRef.current = null }, 2000)
    }
  }, [lang])

  // Detect cameras on mount
  React.useEffect(() => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrorMsg(lang === "id"
        ? "Browser tidak mendukung akses kamera. Pakai Chrome/Safari modern."
        : "Browser does not support camera. Use modern Chrome/Safari.")
      return
    }
    Html5Qrcode.getCameras()
      .then((devices) => {
        setCameras(devices || [])
        if (devices && devices.length > 0) {
          const back = devices.find((d) => /back|rear|environment/i.test(d.label))
          setSelectedCamId(back?.id || devices[devices.length - 1].id)
        } else {
          setErrorMsg(lang === "id" ? "Tidak ada kamera terdeteksi di device ini." : "No camera found on this device.")
        }
      })
      .catch((e) => {
        setErrorMsg(
          (lang === "id" ? "Gagal mendeteksi kamera: " : "Failed to detect camera: ") +
          (e?.message || e)
        )
      })
  }, [lang])

  const start = async () => {
    if (scanning || !selectedCamId) return
    setScanning(true)
    setLastResult(null)
    setErrorMsg(null)
    try {
      const html5 = new Html5Qrcode("qr-reader")
      html5QrRef.current = html5
      await html5.start(
        selectedCamId,
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => handleScanned(decodedText),
        () => { /* ignore per-frame decode misses */ }
      )
    } catch (e) {
      setScanning(false)
      const msg = e?.message || String(e)
      setErrorMsg(
        (lang === "id" ? "Gagal mulai kamera: " : "Failed to start camera: ") + msg
      )
      toast.error(msg)
    }
  }

  const stop = React.useCallback(async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop() } catch {}
      try { await html5QrRef.current.clear() } catch {}
      html5QrRef.current = null
    }
    setScanning(false)
    lastTokenRef.current = null
  }, [])

  React.useEffect(() => () => { stop() }, [stop])

  const kirimKode = async (e) => {
    e.preventDefault()
    if (kode.length !== 6) return
    setMengirimKode(true)
    try {
      const res = await checkInWithCode(kode, await ambilKoordinat())
      setLastResult({ ok: true, title: res.sessionTitle })
      toast.success(lang === "id" ? `Hadir dicatat: ${res.sessionTitle}` : `Checked in: ${res.sessionTitle}`)
      setKode("")
    } catch (err) {
      const msg = err?.response?.data?.error || err.message
      setLastResult({ ok: false, error: msg })
      toast.error(msg)
    } finally {
      setMengirimKode(false)
    }
  }

  return (
    <div className="main-inner" style={{ maxWidth: 640 }}>
      <div className="page-header">
        <div className="page-header-text">
          <h1 className="h1">{lang === "id" ? "Absensi" : "Attendance"}</h1>
          <div className="muted mt-1">
            {lang === "id"
              ? "Arahkan kamera ke kode QR yang ditampilkan trainer di sesi tatap muka."
              : "Point your camera at the QR shown by your trainer at the in-person session."}
          </div>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Scanner Kamera" : "Camera Scanner"}</div>
          {scanning && <Badge tone="success" dot>Live</Badge>}
        </div>
        <div className="col" style={{ alignItems: "center", padding: 24, gap: 16 }}>
          {cameras.length > 0 && !scanning && (
            <label className="col gap-1" style={{ width: "100%", maxWidth: 480 }}>
              <span className="small">{lang === "id" ? "Pilih Kamera" : "Select Camera"}</span>
              <select
                value={selectedCamId || ""}
                onChange={(e) => setSelectedCamId(e.target.value)}
                style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
              >
                {cameras.map((c) => (
                  <option key={c.id} value={c.id}>{c.label || `Camera ${c.id.slice(0, 6)}`}</option>
                ))}
              </select>
            </label>
          )}

          <div
            id="qr-reader"
            style={{
              width: "100%", maxWidth: 480, minHeight: scanning ? 360 : 0,
              border: scanning ? "1px solid var(--border-subtle)" : "none",
              borderRadius: scanning ? 8 : 0,
              overflow: "hidden",
            }}
          />

          {!scanning ? (
            <Btn variant="primary" icon={<Icon name="cam" size={14}/>}
              onClick={start} disabled={!selectedCamId} style={{ minWidth: 200 }}>
              {lang === "id" ? "Mulai Scan" : "Start Scanning"}
            </Btn>
          ) : (
            <Btn variant="ghost" icon={<Icon name="close" size={14}/>}
              onClick={stop} style={{ minWidth: 200 }}>
              {lang === "id" ? "Hentikan" : "Stop"}
            </Btn>
          )}

          {errorMsg && (
            <div style={{
              width: "100%", background: "var(--amber-10, #FFF8E1)",
              border: "1px solid var(--amber-50, #FBC02D)",
              borderRadius: 8, padding: 12,
            }}>
              <div className="row gap-2">
                <Icon name="alert" size={16} color="var(--amber-60, #F57C00)"/>
                <div className="small">{errorMsg}</div>
              </div>
              <div className="tiny muted mt-2">
                {lang === "id"
                  ? "Tip: pastikan situs diakses via HTTPS, izinkan kamera saat browser meminta. Atau ketik kode 6 digit yang tampil di bawah QR, di kolom bawah."
                  : "Tip: site must be HTTPS, allow camera when prompted. Or type the 6-digit code shown under the QR in the box below."}
              </div>
            </div>
          )}

          {lastResult && (
            <div style={{
              width: "100%",
              background: lastResult.ok ? "var(--green-10, #E6F4EA)" : "var(--red-10, #FBE9E7)",
              border: `1px solid ${lastResult.ok ? "var(--green-50, #34A853)" : "var(--red-50, #EA4335)"}`,
              borderRadius: 8, padding: 16,
            }}>
              {lastResult.ok ? (
                <div className="row gap-2">
                  <Icon name="check" size={20} color="var(--green-60, #137333)"/>
                  <div>
                    <div className="small" style={{ fontWeight: 600 }}>
                      {lang === "id" ? "Kehadiran dicatat!" : "Checked in!"}
                    </div>
                    <div className="tiny muted">{lastResult.title}</div>
                  </div>
                </div>
              ) : (
                <div className="row gap-2">
                  <Icon name="alert" size={20} color="var(--red-60, #B31412)"/>
                  <div>
                    <div className="small" style={{ fontWeight: 600 }}>
                      {lang === "id" ? "Gagal" : "Failed"}
                    </div>
                    <div className="tiny muted">{lastResult.error}</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div style={{
          padding: "12px 20px",
          borderTop: "1px solid var(--border-subtle)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span className="tiny muted">
            {lang === "id"
              ? "QR aktif hanya ~30 detik. Jika gagal, minta trainer untuk menampilkan ulang."
              : "QR is valid for ~30 seconds. If failed, ask trainer to refresh."}
          </span>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Atau Ketik Kode" : "Or Enter Code"}</div>
        </div>
        <form className="col gap-3" style={{ padding: 16 }} onSubmit={kirimKode}>
          <span className="tiny muted">
            {lang === "id"
              ? "Kalau kamera tidak jalan: ketik kode 6 digit yang tampil di bawah QR di layar trainer. Kodenya berganti bersama QR tiap ~30 detik."
              : "If your camera doesn't work: type the 6-digit code shown under the QR on the trainer's screen. It changes with the QR every ~30 seconds."}
          </span>
          <div className="row gap-2">
            <input
              value={kode}
              onChange={(e) => setKode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              aria-label={lang === "id" ? "Kode absensi 6 digit" : "6-digit attendance code"}
              style={{
                flex: 1, height: 40, padding: "0 10px",
                border: "1px solid var(--border-strong)",
                fontFamily: "monospace", fontSize: 20, letterSpacing: "0.3em",
              }}
            />
            <Btn type="submit" variant="primary" size="sm"
              disabled={kode.length !== 6 || mengirimKode}>
              {mengirimKode
                ? (lang === "id" ? "Mengirim..." : "Sending...")
                : (lang === "id" ? "Kirim" : "Submit")}
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}
